import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { errorMessage, fetchUsers, isUnauthorized } from '../../api';
import ClearConfirm from './ClearConfirm';

// Bs 1.500, Bs 150,50
const formatAmount = (amount) =>
  `Bs ${Number(amount || 0).toLocaleString('es-BO', { maximumFractionDigits: 2 })}`;

const formatPhone = (phone) => `+${phone}`;

// Excel in Spanish reads ";" as the column separator, and the BOM keeps the accents
const CSV_COLUMNS = [
  ['Celular', (user) => formatPhone(user.phone)],
  ['Nombre', (user) => user.name || ''],
  ['Nickname', (user) => user.nickname || ''],
  ['Registrado', (user) => (user.registered ? 'sí' : 'no')],
  ['Compras', (user) => user.purchases],
  ['Total', (user) => user.total],
  ['Entró', (user) => user.joinedAt || ''],
  ['Última compra', (user) => user.lastPurchaseAt || ''],
];

const csvCell = (value) => `"${String(value).replace(/"/g, '""')}"`;

const toCsv = (users) =>
  `﻿${[
    CSV_COLUMNS.map(([header]) => csvCell(header)).join(';'),
    ...users.map((user) => CSV_COLUMNS.map(([, value]) => csvCell(value(user))).join(';')),
  ].join('\r\n')}\r\n`;

const download = (users) => {
  const url = URL.createObjectURL(new Blob([toCsv(users)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `usuarios-bolfit-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// The system goes from one event to the next: what stays here would mix the people and the
// purchases of both. Each clean-up says what it takes before it asks for the word
const CLEAN_UPS = {
  users: {
    warning: ({ people }) =>
      `Borra a las ${people} personas de esta lista, con todo lo suyo: registros, nicknames, sesiones, compras y silenciados. Quien quiera entrar se registra de nuevo. No se puede deshacer: descargá el CSV antes.`,
    confirmLabel: 'Borrar todos los usuarios',
  },
  buyers: {
    warning: ({ buyers }) =>
      `Borra las compras de ${buyers} compradores: salen del jackpot de clientes y quien solo compró sale de esta lista. Quien se registró sigue registrado, sin sus compras. No se puede deshacer: descargá el CSV antes.`,
    confirmLabel: 'Borrar todos los compradores',
  },
};

const UsersReport = ({ adminKey, onUnauthorized }) => {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [clearing, setClearing] = useState(null); // 'users' | 'buyers' while its question is open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await fetchUsers(adminKey));
      setError(null);
    } catch (err) {
      if (isUnauthorized(err)) onUnauthorized();
      else setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [adminKey, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle || !users) return users || [];
    return users.filter(({ phone, name, nickname }) =>
      [phone, name, nickname].some((field) => field?.toLowerCase().includes(needle))
    );
  }, [users, search]);

  const buyers = (users || []).filter((user) => user.purchases).length;

  return (
    <section className='admin__block'>
      <div className='report__head'>
        <h2 className='admin__block-title'>
          Usuarios del evento {users && <span className='report__count'>{users.length}</span>}
        </h2>
        <div className='report__actions'>
          <button type='button' className='admin__signout' onClick={load} disabled={loading}>
            {loading ? 'Cargando…' : 'Actualizar'}
          </button>
          <button type='button' className='admin__signout' onClick={() => download(shown)} disabled={!shown.length}>
            Descargar CSV
          </button>
          <button
            type='button'
            className='admin__signout admin__signout--danger'
            onClick={() => setClearing('buyers')}
            disabled={!buyers || Boolean(clearing)}
          >
            Limpiar compradores
          </button>
          <button
            type='button'
            className='admin__signout admin__signout--danger'
            onClick={() => setClearing('users')}
            disabled={!users?.length || Boolean(clearing)}
          >
            Limpiar usuarios
          </button>
        </div>
      </div>

      {clearing && (
        <ClearConfirm
          adminKey={adminKey}
          scope={clearing}
          warning={CLEAN_UPS[clearing].warning({ people: users.length, buyers })}
          confirmLabel={CLEAN_UPS[clearing].confirmLabel}
          onCleared={() => {
            setClearing(null);
            load();
          }}
          onCancel={() => setClearing(null)}
          onUnauthorized={onUnauthorized}
        />
      )}

      <p className='report__note'>
        Único lugar donde se ven celular y nombre. En el chat y en los sorteos solo aparece el nickname.
      </p>

      <input
        className='input report__search'
        type='search'
        placeholder='Buscar por celular, nombre o nickname…'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {error && <p className='form-error'>{error}</p>}

      {users && !shown.length ? (
        <p className='admin__empty'>{users.length ? 'Nadie coincide con la búsqueda.' : 'Todavía no hay nadie en el evento.'}</p>
      ) : (
        <div className='report__scroll'>
          <table className='report'>
            <thead>
              <tr>
                <th>Celular</th>
                <th>Nombre</th>
                <th>Nickname</th>
                <th className='report__num'>Compras</th>
                <th className='report__num'>Total</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((user) => (
                <tr key={user.phone}>
                  <td className='report__phone'>{formatPhone(user.phone)}</td>
                  <td>{user.name || <span className='report__missing'>sin compras</span>}</td>
                  <td>
                    {user.nickname || <span className='report__missing'>sin registrar</span>}
                  </td>
                  <td className='report__num'>{user.purchases || ''}</td>
                  <td className='report__num'>{user.purchases ? formatAmount(user.total) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default UsersReport;
